<template>
  <div class="symbology-histogram" title="Arrastrar la esquina para cambiar el tamaño">
    <apexchart width="100%" height="100%" type="bar" :options="chartOptions" :series="series"></apexchart>
  </div>
</template>

<script>
import { esriColor } from "@/services/layers/symbology";

// Las features bajo minValue o sobre el último corte NO se dibujan en el mapa
// (createStyleFunction devuelve fallbackStyle, que en el camino de simbología
// elegida es undefined a propósito). En el histograma esa masa tiene que SEGUIR
// VIÉNDOSE: es justo la evidencia para decidir dónde poner el piso. Gris dice
// "esto existe y hoy no se dibuja"; una barra ausente lo escondería.
const EXCLUDED_COLOR = "#cccccc";

export default {
  name: "SymbologyHistogram",
  props: {
    // [{ from, to, count }] — cantidad y ancho calculados por la vista a partir del número
    // de observaciones (Sturges) sobre el rango visible, ya recortado de outliers.
    // count null = el conteo no se pudo obtener: ApexCharts no dibuja barra (hueco),
    // y la vista lo informa aparte. No es lo mismo que count 0.
    bins: { type: Array, default: () => [] },
    classBreakInfos: { type: Array, default: () => [] },
    minValue: { type: [Number, String], default: "" },
    decimals: { type: Number, default: 2 },
    // Nombre del campo, sólo para la etiqueta del eje X. La spec prohíbe inventar una
    // unidad, así que la etiqueta es el nombre crudo del campo y nada más.
    field: { type: String, default: "" },
  },
  computed: {
    // Denominador = suma de los conteos NO nulos, no un total pedido aparte: es lo que
    // efectivamente se dibuja, así que la altura de las barras y el eje Y no se contradicen
    // cuando algún bin cae o cuando hay outliers recortados.
    totalCount() {
      return this.bins.reduce((sum, bin) => sum + (bin.count == null ? 0 : bin.count), 0);
    },
    // Densidad = proporción de las observaciones VISIBLES por unidad de valor (el denominador
    // es totalCount, que excluye lo recortado). Con bins de ancho uniforme
    // es un reescalado del conteo; el valor de la densidad acá es que el eje Y conserva su
    // significado cuando la cantidad de bins cambia con el tamaño del conjunto — que es justo
    // lo que introdujo el binning dinámico.
    series() {
      const total = this.totalCount;
      return [{
        name: "Densidad",
        data: this.bins.map(bin => {
          if (bin.count == null) return null;
          const width = bin.to - bin.from;
          if (!total || !(width > 0)) return null;
          return bin.count / (total * width);
        }),
      }];
    },
    // Mismo matcher que createStyleFunction (symbology.js:274-284): copia
    // ordenada propia, piso -Infinity cuando no hay minValue, findIndex sobre
    // `value <= classMaxValue`. Si divergiera, el gráfico mentiría sobre el mapa.
    barColors() {
      const infos = [...this.classBreakInfos].sort(
        (a, b) => Number(a.classMaxValue) - Number(b.classMaxValue)
      );
      // '' / null / no-finito = sin piso, mismo contrato que setRendererType
      // (LayersSymbologies.vue) y el -Infinity de createStyleFunction: Number('')
      // da 0, no NaN, así que el prop crudo se chequea ANTES de coercionar.
      const raw = this.minValue;
      const floor = raw === "" || raw == null ? NaN : Number(raw);
      const bottom = Number.isFinite(floor) ? floor : -Infinity;
      return this.bins.map(bin => {
        // El centro del bin, no su borde: el costo es ±1 bin de imprecisión
        // cuando un corte cae dentro de una barra — aceptable en una ayuda de
        // diseño, y a cambio no hay que configurar un eje X numérico.
        const center = (bin.from + bin.to) / 2;
        if (center < bottom) return EXCLUDED_COLOR;
        const index = infos.findIndex(info => center <= Number(info.classMaxValue));
        if (index === -1) return EXCLUDED_COLOR;
        const symbol = infos[index].symbol;
        // Un esriSFS pinta con .color; un esriSLS también. El editor sólo
        // maneja punto/línea/polígono, y en los tres el color de relleno vive ahí.
        // Un esriPMS (Ícono) NO trae color: esriColor devolvería el string
        // "rgba(0,0,0,0)" —truthy— y la barra saldría transparente. El contrato
        // de esriColor es correcto para OpenLayers; el que asumió de más era este
        // llamador.
        const color = symbol && symbol.color;
        return Array.isArray(color) ? esriColor(color) : EXCLUDED_COLOR;
      });
    },
    chartOptions() {
      const labels = this.bins.map(bin => this.formatRange(bin));
      const counts = this.bins.map(bin => bin.count);
      return {
        chart: { type: "bar", toolbar: { show: false }, animations: { enabled: false } },
        // distributed: un color por barra en vez de uno por serie.
        plotOptions: { bar: { distributed: true, columnWidth: "95%" } },
        colors: this.barColors,
        dataLabels: { enabled: false },
        // Con distributed, ApexCharts pone una entrada de leyenda por barra:
        // una entrada por bin que repite lo que ya dice la lista de rangos.
        legend: { show: false },
        xaxis: {
          categories: labels,
          labels: { show: false },
          axisTicks: { show: false },
          // Sin unidad a propósito: los datos vienen de bases de datos que no la traen y la
          // spec prohíbe inventarla.
          title: { text: this.field },
        },
        // Math.round mentía bajo densidad: los valores son fraccionarios y todos caerían en 0.
        yaxis: {
          title: { text: "Densidad" },
          labels: {
            // Notación científica SÓLO cuando la estándar sería ilegible: con bins anchos (un
            // campo de población da bins de ~3e4) la densidad cae a ~5e-9 y toLocaleString
            // estándar imprime "0,0000000056" — doce caracteres por marca, que se comen el
            // ancho útil y hacen que dos marcas vecinas se lean iguales. Pero un campo de
            // razón o índice (rango ~1) da densidades de orden 1, donde la científica
            // empeoraría un "2,99" legible convirtiéndolo en "3E0". De ahí el umbral.
            // Un motor que no conozca `notation` ignora la llave y cae al formato estándar.
            // El cero se escribe a mano: ApexCharts ancla el eje de un gráfico de barras en 0
            // y la marca de la base saldría "0E0", que se lee como un error de formato.
            formatter: value => {
              const density = Number(value);
              if (density === 0) return "0";
              return Math.abs(density) < 0.001
                ? density.toLocaleString("es-CL", { notation: "scientific", maximumSignificantDigits: 2 })
                : density.toLocaleString("es-CL", { maximumSignificantDigits: 3 });
            },
          },
        },
        tooltip: {
          // La densidad es lo que se dibuja, pero el conteo crudo es lo que el usuario puede
          // verificar contra el servicio: se muestran los dos.
          y: {
            formatter: (value, opts) => {
              const count = counts[opts.dataPointIndex];
              const density = Number(value).toLocaleString("es-CL", { maximumSignificantDigits: 3 });
              return count == null ? density : `${density} (${count} features)`;
            },
          },
          x: { formatter: (_, opts) => labels[opts.dataPointIndex] },
        },
      };
    },
  },
  methods: {
    formatRange(bin) {
      const format = value =>
        Number(value).toLocaleString("es-CL", {
          minimumFractionDigits: this.decimals,
          maximumFractionDigits: this.decimals,
        });
      return `${format(bin.from)} - ${format(bin.to)}`;
    },
  },
};
</script>

<style scoped>
/* El alto vivía en el atributo height="180" del <apexchart>; pasa a CSS para que
   el usuario pueda arrastrar. ApexCharts 3.54 (el lock resuelve 3.54.1, no el
   ^3.10.1 declarado) ya trae redrawOnParentResize con un ResizeObserver sobre
   el parentNode del div que renderiza vue-apexcharts — es decir, sobre este
   elemento — así que el resize nativo basta y no hace falta JS.
   Sin padding a propósito: getDimensions() lo resta del alto que mide.
   El tamaño no se persiste: el v-if de LayersSymbologies desmonta el componente
   al cerrar "Ver distribución", y cada apertura vuelve a 240px. */
.symbology-histogram {
  width: 480px;
  max-width: 100%;
  min-width: 280px;
  height: 240px;
  min-height: 140px;
  max-height: 60vh;
  /* resize se ignora si overflow es visible: el par es obligatorio. */
  resize: both;
  overflow: hidden;
}
</style>
